import {assertAndContinue} from 'Asserts';
import {assertNever, NumericInputState} from 'util/Types';

import {RegressionParameterConstraintType, RegressionParameterName} from '../RegressionTypes';
import {
  RegressionConfigModalParameterConstraint,
  RegressionConfigModalParameterInitialValue,
  RegressionConfigModalState,
} from './RegressionConfigModalTypes';

function numericInputValueIsComplete({isValid, numericValue}: NumericInputState): boolean {
  return isValid && Number.isFinite(numericValue);
}

export function getParameterConstraintInputStates(
  constraint: RegressionConfigModalParameterConstraint
): Array<NumericInputState> {
  const {type} = constraint;
  switch (type) {
    case RegressionParameterConstraintType.NONE:
      return [];
    case RegressionParameterConstraintType.EQUAL:
      return [constraint.value];
    case RegressionParameterConstraintType.LESS_THAN:
      return [constraint.upperBound];
    case RegressionParameterConstraintType.GREATER_THAN:
      return [constraint.lowerBound];
    case RegressionParameterConstraintType.BETWEEN:
      return [constraint.lowerBound, constraint.upperBound];
    default:
      assertAndContinue(false, `Unexpected constraint ${assertNever(type)}`);
      return [];
  }
}

export function regressionParameterConstraintIsValid(
  parameterName: RegressionParameterName,
  constraint: RegressionConfigModalParameterConstraint
): boolean {
  const inputStates = getParameterConstraintInputStates(constraint);
  if (
    constraint.type === RegressionParameterConstraintType.BETWEEN &&
    constraint.lowerBound.numericValue! >= constraint.upperBound.numericValue!
  ) {
    return false;
  }
  return inputStates.every(
    (inputState) =>
      numericInputValueIsComplete(inputState) && !hasParameterValueError(parameterName, inputState.value)
  );
}

function regressionParameterInitialValueIsValid(
  parameterName: RegressionParameterName,
  initialValue: RegressionConfigModalParameterInitialValue
): boolean {
  return (
    !initialValue.userDefined ||
    (numericInputValueIsComplete(initialValue.value) &&
      !hasParameterValueError(parameterName, initialValue.value.value))
  );
}

export default function regressionConfigModalStateIsValid(modalState: RegressionConfigModalState): boolean {
  return modalState.parameterCustomizations.every(
    ({name, constraint, initialValue}) =>
      regressionParameterConstraintIsValid(name, constraint) &&
      regressionParameterInitialValueIsValid(name, initialValue)
  );
}

const PARAMETER_NAME_TO_ERROR_MESSAGE_GENERATOR: Partial<{
  [key in RegressionParameterName]: (value: string) => string | null;
}> = {
  [RegressionParameterName.IC50]: (value) => {
    const numericValue = parseFloat(value);
    if (!Number.isNaN(numericValue) && numericValue <= 0) {
      return 'IC50 values must be positive.';
    }
    return null;
  },
};

export function getParameterValueErrorMessage(
  parameterName: RegressionParameterName,
  value: string
): string | null {
  const generator = PARAMETER_NAME_TO_ERROR_MESSAGE_GENERATOR[parameterName];
  return generator != null ? generator(value) : null;
}

function hasParameterValueError(parameterName: RegressionParameterName, value: string): boolean {
  return getParameterValueErrorMessage(parameterName, value) != null;
}
